/**
 * Stripe Provider
 *
 * Wraps the app with Stripe context for payment processing.
 * In mock mode (no SDK installed), provides a passthrough wrapper.
 * When real SDK is installed, initializes StripeProvider properly.
 */

import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import * as Linking from 'expo-linking';
import { openExternalUrl } from '@/lib/linking';
import { reportError } from '@/lib/errors';

// ============================================================================
// Types
// ============================================================================

interface StripeContextValue {
  isInitialized: boolean;
  isMockMode: boolean;
  publishableKey: string | null;
  // Payment sheet methods (mock implementations)
  initPaymentSheet: (params: InitPaymentSheetParams) => Promise<PaymentSheetResult>;
  presentPaymentSheet: () => Promise<PaymentSheetResult>;
  // Customer portal
  openCustomerPortal: (url: string) => Promise<void>;
}

export interface InitPaymentSheetParams {
  paymentIntentClientSecret?: string;
  setupIntentClientSecret?: string;
  customerEphemeralKeySecret?: string;
  customerId?: string;
  merchantDisplayName?: string;
  allowsDelayedPaymentMethods?: boolean;
  returnURL?: string;
}

export interface PaymentSheetResult {
  error?: {
    code: string;
    message: string;
  };
}

// ============================================================================
// Context
// ============================================================================

const StripeContext = createContext<StripeContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

interface StripeProviderProps {
  children: ReactNode;
  publishableKey?: string;
  merchantIdentifier?: string;
}

// ============================================================================
// Mock Stripe Implementation
// ============================================================================

/**
 * Mock payment sheet initialization
 * Simulates the Stripe PaymentSheet flow
 */
async function mockInitPaymentSheet(
  _params: InitPaymentSheetParams
): Promise<PaymentSheetResult> {
  // Simulate initialization delay
  await new Promise((resolve) => setTimeout(resolve, 200));
  return {};
}

/**
 * Mock payment sheet presentation
 * Simulates successful payment
 */
async function mockPresentPaymentSheet(): Promise<PaymentSheetResult> {
  // Simulate payment processing delay
  await new Promise((resolve) => setTimeout(resolve, 1000));

  // In mock mode, always succeed
  // In real implementation, this would show the Stripe payment UI
  return {};
}

/**
 * Open customer portal in browser
 */
async function openCustomerPortal(url: string): Promise<void> {
  try {
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await openExternalUrl(url, 'StripeProvider.openCheckout');
    } else {
      console.warn('Cannot open customer portal URL:', url);
    }
  } catch (error) {
    reportError(error, { context: 'StripeProvider.openCustomerPortal' });
    throw error;
  }
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * StripeProvider - Initializes Stripe for payment processing
 *
 * Usage:
 * ```tsx
 * <StripeProvider publishableKey={process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY}>
 *   <App />
 * </StripeProvider>
 * ```
 *
 * In development/mock mode (no publishable key), provides mock implementations
 * that simulate the payment flow for testing.
 */
export function StripeProvider({
  children,
  publishableKey,
  merchantIdentifier: _merchantIdentifier,
}: StripeProviderProps) {
  // Check if we're in mock mode (no publishable key or no SDK)
  const isMockMode = !publishableKey || publishableKey.startsWith('pk_mock');

  const urlScheme = useMemo(() => {
    try {
      return Linking.createURL('');
    } catch {
      return 'inverio://';
    }
  }, []);

  const contextValue = useMemo<StripeContextValue>(
    () => ({
      isInitialized: true,
      isMockMode,
      publishableKey: publishableKey ?? null,
      initPaymentSheet: mockInitPaymentSheet,
      presentPaymentSheet: mockPresentPaymentSheet,
      openCustomerPortal,
    }),
    [isMockMode, publishableKey]
  );

  // Log initialization status
  if (__DEV__) {
    if (isMockMode) {
      console.log('[Stripe] Running in mock mode - payments will be simulated');
    } else {
      console.log('[Stripe] Initialized with publishable key');
    }
  }

  return (
    <StripeContext.Provider value={contextValue}>
      {children}
    </StripeContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access Stripe context
 */
export function useStripeContext(): StripeContextValue {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripeContext must be used within a StripeProvider');
  }
  return context;
}

/**
 * Hook to check if Stripe is in mock mode
 */
export function useIsMockStripe(): boolean {
  const context = useContext(StripeContext);
  return context?.isMockMode ?? true;
}
